/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React from 'react';
import { AIGeneralBlockCard } from '../../AIGeneralBlockCard';
import './UsageMetricRendererStyles.scss';
const baseClass = 'qmb-ui-UsageMetricRenderer';
/**
 * Formats a number with thousands separators.
 * e.g. 12345 → "12,345"
 */
function formatTokens(n) {
    return n.toLocaleString('en-US');
}
/**
 * Formats an ISO date string (e.g. "2026-06-01") to a human label
 * like "June 1". Returns an empty string when the input is missing or
 * malformed (defensive guard — keeps the renderer from crashing if the
 * payload arrives without a reset_date for any reason).
 */
function humanizeResetDate(isoDate) {
    if (typeof isoDate !== 'string' || isoDate.length === 0)
        return '';
    const parts = isoDate.split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n)))
        return '';
    const [year, month, day] = parts;
    // Use UTC to avoid timezone shifts that could change the day.
    const date = new Date(Date.UTC(year, month - 1, day));
    // Reject overflow dates (e.g. month 13 or day 32) that Date.UTC normalizes silently.
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
        return '';
    }
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });
}
/**
 * True usage percentage (integer, floored at 0, NOT clamped above 100).
 * Exempt / grandfathered users legitimately run past their cap, so the number
 * we display reflects real usage (e.g. 104%). The progress-bar fill is clamped
 * separately (see fillWidth) so the bar never overflows its container.
 */
function computePercent(tokensUsed, tokensLimit) {
    if (tokensLimit <= 0)
        return 0;
    return Math.max(Math.round((tokensUsed / tokensLimit) * 100), 0);
}
export function UsageMetricRenderer({ block }) {
    var _a;
    const b = block;
    const tokensUsed = typeof b.tokens_used === 'number' ? b.tokens_used : 0;
    const tokensLimit = typeof b.tokens_limit === 'number' ? b.tokens_limit : null;
    const percent = tokensLimit !== null ? computePercent(tokensUsed, tokensLimit) : null;
    const resetLabel = humanizeResetDate(b.reset_date);
    const periodLabel = (_a = b.period_label) !== null && _a !== void 0 ? _a : '';
    const summary = percent !== null
        ? `${formatTokens(tokensUsed)} of ${formatTokens(tokensLimit !== null && tokensLimit !== void 0 ? tokensLimit : 0)} tokens used in ${periodLabel} (${percent}%)`
        : `${formatTokens(tokensUsed)} tokens used in ${periodLabel}`;
    const copyText = resetLabel ? `${summary}. Resets ${resetLabel}.` : `${summary}.`;
    // Bar fill is capped at 100% so it never overflows its track, even when the
    // displayed percentage (above) exceeds the cap for an exempt user.
    const fillWidth = percent !== null ? `${Math.min(percent, 100)}%` : '0%';
    return (React.createElement(AIGeneralBlockCard, { sections: [
            {
                label: 'Usage',
                content: (React.createElement("div", { className: baseClass },
                    React.createElement("div", { className: `${baseClass}__headline` },
                        React.createElement("strong", { className: `${baseClass}__tokens` }, formatTokens(tokensUsed)),
                        tokensLimit !== null && (React.createElement("span", { className: `${baseClass}__limit` }, ` / ${formatTokens(tokensLimit)}`)),
                        React.createElement("span", { className: `${baseClass}__suffix` }, ` tokens used in ${periodLabel}`),
                        percent !== null && React.createElement("span", { className: `${baseClass}__percent` }, ` · ${percent}%`)),
                    React.createElement("div", { className: `${baseClass}__bar`, role: "progressbar", "aria-valuemin": 0, "aria-valuemax": tokensLimit !== null && tokensLimit !== void 0 ? tokensLimit : undefined, "aria-valuenow": percent !== null && tokensLimit !== null
                            ? Math.min(Math.max(tokensUsed, 0), tokensLimit)
                            : undefined, "aria-label": summary },
                        React.createElement("div", { className: `${baseClass}__bar-fill`, style: { width: fillWidth } })),
                    resetLabel && React.createElement("div", { className: `${baseClass}__reset` }, `Resets ${resetLabel}`))),
                copyText
            }
        ] }));
}
